import React, { useState, useEffect } from 'react';
import API from '../api';
import {
  Wallet, Receipt, TrendingUp, TrendingDown, AlertTriangle, Calendar,
  Plus, Trash2, FileDown, CreditCard, Edit3, X, Search, Filter, PieChart, DollarSign, FileCode2, AlertCircle, CheckCircle2, Upload, Link2, Unlink, Zap, Eye, Download, Send, Mail, Bell, AlertTriangle as AlertTri, Calendar as CalIcon, FileUp, RefreshCw
} from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';
import FattureAttivePage from './FattureAttivePage';
import FatturePassivePage from './FatturePassivePage';
import SdiBadge from '../components/SdiBadge';

const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const TIPO_LABEL = { fattura: 'Fattura', proforma: 'Proforma', nota_credito: 'Nota Credito' };
const STATO_PAG = {
  pagata: { label: 'Pagata', color: 'bg-green-100 text-green-800' },
  parziale: { label: 'Parziale', color: 'bg-yellow-100 text-yellow-800' },
  non_pagata: { label: 'Non pagata', color: 'bg-slate-100 text-slate-700' },
  scaduta: { label: 'Scaduta', color: 'bg-red-100 text-red-800' },
  annullata: { label: 'Annullata', color: 'bg-slate-200 text-slate-500' },
};
const CATEGORIE_SPESA = [
  { v: 'manodopera', l: 'Manodopera' },
  { v: 'materiali', l: 'Materiali' },
  { v: 'noli', l: 'Noli/Attrezzature' },
  { v: 'utenze', l: 'Utenze' },
  { v: 'affitto', l: 'Affitto' },
  { v: 'carburante', l: 'Carburante' },
  { v: 'trasporti', l: 'Trasporti' },
  { v: 'consulenze', l: 'Consulenze' },
  { v: 'tasse', l: 'Tasse e tributi' },
  { v: 'assicurazioni', l: 'Assicurazioni' },
  { v: 'altro', l: 'Altro' },
];

export default function ContabilitaPage() {
  const [tab, setTab] = useState('overview');
  const [anno, setAnno] = useState(new Date().getFullYear());

  const tabs = [
    { id: 'overview', label: 'Panoramica', icon: PieChart },
    { id: 'fatture', label: 'Fatture Emesse', icon: Receipt },
    { id: 'fatture_attive_sdi', label: 'Fatture Attive (SDI)', icon: FileUp },
    { id: 'fatture_passive_sdi', label: 'Fatture Passive (SDI)', icon: FileDown },
    { id: 'fatture_passive', label: 'Upload XML Passive', icon: FileCode2 },
    { id: 'spese', label: 'Spese / Costi', icon: TrendingDown },
    { id: 'scadenzario', label: 'Scadenzario', icon: Calendar },
    { id: 'iva', label: 'Report IVA', icon: DollarSign },
    { id: 'notifiche', label: 'Notifiche', icon: AlertTriangle },
  ];

  return (
    <div className="animate-fade-in" data-testid="contabilita-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Wallet size={26} className="text-primary" /> Contabilita Aziendale
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Fatture, pagamenti, spese e andamento finanziario</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-500">Anno:</label>
          <select value={anno} onChange={(e) => setAnno(Number(e.target.value))} data-testid="anno-select"
            className="px-3 py-1.5 border border-slate-300 rounded-md text-sm font-bold">
            {[0, 1, 2].map(d => <option key={d} value={new Date().getFullYear() - d}>{new Date().getFullYear() - d}</option>)}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto" data-testid="contabilita-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-all border-b-2 ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab anno={anno} />}
      {tab === 'fatture' && <FattureTab anno={anno} />}
      {tab === 'fatture_attive_sdi' && <FattureAttivePage />}
      {tab === 'fatture_passive_sdi' && <FatturePassivePage />}
      {tab === 'fatture_passive' && <FatturePassiveTab anno={anno} />}
      {tab === 'spese' && <SpeseTab anno={anno} />}
      {tab === 'scadenzario' && <ScadenzarioTab />}
      {tab === 'iva' && <IvaTab anno={anno} />}
      {tab === 'notifiche' && <NotificheTab />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// OVERVIEW
// ═══════════════════════════════════════════════════════════════
function OverviewTab({ anno }) {
  const [ov, setOv] = useState(null);
  const [cf, setCf] = useState(null);

  useEffect(() => {
    API.get(`/api/contabilita/overview?anno=${anno}`).then(r => setOv(r.data)).catch(() => {});
    API.get(`/api/contabilita/cashflow?anno=${anno}`).then(r => setCf(r.data)).catch(() => {});
  }, [anno]);

  if (!ov) return <div className="text-slate-400 py-8 text-center">Caricamento...</div>;

  const maxMese = cf ? Math.max(1, ...cf.mesi.map(m => Math.max(m.entrate, m.uscite))) : 1;

  return (
    <div className="space-y-5" data-testid="overview-tab">
      {/* KPI Finanziari principali */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiFinance icon={TrendingUp} label="Ricavi netti" value={fmt(ov.ricavi_netti)} sub={`${ov.num_fatture} fatture`} color="green" />
        <KpiFinance icon={TrendingDown} label="Costi" value={fmt(ov.costi_imponibile)} sub={`${ov.num_spese} spese`} color="red" />
        <KpiFinance icon={DollarSign} label="Utile lordo" value={fmt(ov.utile_lordo)} sub={`Margine ${ov.margine_pct}%`} color={ov.utile_lordo >= 0 ? 'primary' : 'red'} />
        <KpiFinance icon={AlertTriangle} label="Da incassare" value={fmt(ov.da_incassare)} sub={ov.importo_scaduto > 0 ? `${fmt(ov.importo_scaduto)} scaduti` : 'Nessuno scaduto'} color={ov.importo_scaduto > 0 ? 'red' : 'amber'} />
      </div>

      {/* Cashflow mensile */}
      <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="cashflow-chart">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold">Cashflow Mensile {anno}</h3>
          {cf && (
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded-sm inline-block" />Entrate {fmt(cf.totale_entrate)}</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded-sm inline-block" />Uscite {fmt(cf.totale_uscite)}</span>
              <span className="font-bold">Saldo: <span className={cf.saldo >= 0 ? 'text-green-600' : 'text-red-600'}>{fmt(cf.saldo)}</span></span>
            </div>
          )}
        </div>
        {cf ? (
          <div className="flex items-end gap-2 h-56 pt-4">
            {cf.mesi.map(m => (
              <div key={m.mese} className="flex-1 flex flex-col items-center gap-1" title={`${m.nome}: Entrate ${fmt(m.entrate)}, Uscite ${fmt(m.uscite)}`}>
                <div className="w-full flex gap-0.5 h-full items-end">
                  <div className="flex-1 bg-green-500 rounded-t transition-all hover:opacity-80"
                    style={{ height: `${(m.entrate / maxMese) * 100}%`, minHeight: m.entrate > 0 ? '2px' : '0' }} />
                  <div className="flex-1 bg-red-400 rounded-t transition-all hover:opacity-80"
                    style={{ height: `${(m.uscite / maxMese) * 100}%`, minHeight: m.uscite > 0 ? '2px' : '0' }} />
                </div>
                <span className="text-[10px] text-slate-500 font-medium">{m.nome}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400 py-8 text-center">Caricamento cashflow...</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* IVA */}
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="iva-overview">
          <h3 className="text-base font-bold mb-4 flex items-center gap-2"><Receipt size={16} className="text-primary" /> Situazione IVA</h3>
          <div className="space-y-3">
            <Row label="IVA su vendite" value={fmt(ov.iva_vendite)} />
            <Row label="IVA su acquisti" value={fmt(ov.iva_acquisti)} />
            <div className="border-t border-slate-200 pt-3">
              <Row label="IVA da versare" value={fmt(ov.iva_dovuta)} bold color={ov.iva_dovuta > 0 ? 'text-red-600' : 'text-green-600'} />
            </div>
          </div>
        </div>

        {/* Costi per categoria */}
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="costi-categoria">
          <h3 className="text-base font-bold mb-4 flex items-center gap-2"><PieChart size={16} className="text-accent-warning" /> Costi per Categoria</h3>
          {ov.costi_per_categoria.length > 0 ? ov.costi_per_categoria.map((c, i) => {
            const max = ov.costi_per_categoria[0].importo || 1;
            const pct = Math.round(c.importo / max * 100);
            const label = CATEGORIE_SPESA.find(x => x.v === c.categoria)?.l || c.categoria;
            return (
              <div key={c.categoria || i} className="mb-2 last:mb-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold">{label}</span>
                  <span className="font-mono font-bold text-sm">{fmt(c.importo)}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div className="bg-accent-warning rounded-full h-2 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          }) : <p className="text-sm text-slate-400 py-4">Nessuna spesa registrata</p>}
        </div>
      </div>
    </div>
  );
}

function KpiFinance({ icon: Icon, label, value, sub, color }) {
  const colorMap = {
    green: 'bg-green-50 border-green-200 text-green-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    primary: 'bg-primary border-primary text-primary',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
  };
  return (
    <div className={`rounded-md p-4 border ${colorMap[color]}`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={18} />
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
      </div>
      <p className="text-xl sm:text-2xl font-black font-mono">{value}</p>
      <p className="text-[11px] opacity-70 mt-0.5">{sub}</p>
    </div>
  );
}

function Row({ label, value, bold, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${bold ? 'font-bold' : 'text-slate-600'}`}>{label}</span>
      <span className={`font-mono ${bold ? 'font-black text-base' : 'font-bold text-sm'} ${color || ''}`}>{value}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FATTURE
// ═══════════════════════════════════════════════════════════════
function FattureTab({ anno }) {
  const confirm = useConfirm();
  const [fatture, setFatture] = useState([]);
  const [filtri, setFiltri] = useState({ stato_pagamento: '', tipo: '', search: '' });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ anno: String(anno) });
      if (filtri.stato_pagamento) params.set('stato_pagamento', filtri.stato_pagamento);
      if (filtri.tipo) params.set('tipo', filtri.tipo);
      const r = await API.get(`/api/fatture?${params.toString()}`);
      setFatture(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [anno, filtri.stato_pagamento, filtri.tipo]);

  const filtered = fatture.filter(f => !filtri.search ||
    (f.numero_fattura || '').toLowerCase().includes(filtri.search.toLowerCase()) ||
    (f.cliente_nome || '').toLowerCase().includes(filtri.search.toLowerCase()) ||
    (f.oggetto || '').toLowerCase().includes(filtri.search.toLowerCase())
  );

  const scaricaPdf = async (f) => {
    try {
      const resp = await API.get(`/api/fatture/${f.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Fattura_${f.numero_fattura}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Errore download PDF'); }
  };

  const scaricaExcel = async (f) => {
    try {
      const resp = await API.get(`/api/fatture/${f.id}/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Fattura_${f.numero_fattura}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Errore download Excel'); }
  };

  const scaricaElencoExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filtri.anno) params.append('anno', filtri.anno);
      if (filtri.stato_pagamento) params.append('stato_pagamento', filtri.stato_pagamento);
      if (filtri.tipo) params.append('tipo', filtri.tipo);
      const resp = await API.get(`/api/fatture/excel/elenco${params.toString() ? '?' + params.toString() : ''}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Fatture_elenco.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Errore export elenco fatture'); }
  };

  const [xmlModal, setXmlModal] = useState(null);
  const [sollecitoModal, setSollecitoModal] = useState(null);

  const apriXmlSdi = (f) => setXmlModal(f);
  const apriSollecito = async (f) => {
    try {
      const r = await API.get(`/api/fatture/${f.id}/sollecito`);
      setSollecitoModal({ ...r.data, fattura_id: f.id, fattura_num: f.numero_fattura });
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore generazione sollecito');
    }
  };

  const pushToFic = async (f) => {
    const action = f.fic_id ? 'aggiornare su FIC e inviare al SDI' : 'inviare su FattureInCloud e trasmettere al SDI';
    if (!await confirm({ title: 'Invio Fattura Elettronica', message: `Vuoi ${action} la fattura ${f.numero_fattura}?`, confirmLabel: 'Invia', variant: 'primary' })) return;
    try {
      const r = await API.post(`/api/fatture/${f.id}/push-fic-and-invia`);
      const eiStatus = r.data?.ei_status || 'inviata';
      alert(`Push completato. Stato SDI: ${eiStatus}` + (r.data?.ei_message ? `\nMessaggio: ${r.data.ei_message}` : ''));
      load();
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const refreshFromFic = async (f) => {
    try {
      const r = await API.post(`/api/sdi/fatture/${f.id}/refresh-from-fic`);
      const a = r.data?.applied || {};
      const changes = [];
      if (a.sdi_stato && a.sdi_stato !== f.sdi_stato) changes.push(`SDI: ${f.sdi_stato || 'n/d'} → ${a.sdi_stato}`);
      if (a.stato_pagamento && a.stato_pagamento !== f.stato_pagamento) changes.push(`Pagamento: ${f.stato_pagamento} → ${a.stato_pagamento}`);
      if (a.importo_pagato) changes.push(`Importo pagato: € ${a.importo_pagato.toLocaleString('it-IT')}`);
      if (a.data_pagamento) changes.push(`Data pagamento: ${new Date(a.data_pagamento).toLocaleDateString('it-IT')}`);
      const msg = changes.length > 0
        ? `Aggiornata da FattureInCloud:\n• ${changes.join('\n• ')}`
        : 'Fattura già allineata con FattureInCloud (nessuna modifica).';
      alert(msg);
      load();
    } catch (e) {
      alert('Errore refresh: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const elimina = async (id) => {
    if (!await confirm({ title: 'Eliminare fattura?', message: 'Verranno cancellati anche tutti i pagamenti collegati. Operazione irreversibile.', confirmLabel: 'Elimina', variant: 'danger' })) return;
    await API.delete(`/api/fatture/${id}`);
    load();
  };

  return (
    <div data-testid="fatture-tab">
      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-md p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Cerca numero, cliente, oggetto..."
            value={filtri.search} onChange={(e) => setFiltri({ ...filtri, search: e.target.value })}
            data-testid="fatture-search"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm" />
        </div>
        <select value={filtri.tipo} onChange={(e) => setFiltri({ ...filtri, tipo: e.target.value })}
          data-testid="filter-tipo"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          <option value="">Tutti i tipi</option>
          <option value="fattura">Fattura</option>
          <option value="proforma">Proforma</option>
          <option value="nota_credito">Nota credito</option>
        </select>
        <select value={filtri.stato_pagamento} onChange={(e) => setFiltri({ ...filtri, stato_pagamento: e.target.value })}
          data-testid="filter-stato"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          <option value="">Tutti gli stati</option>
          <option value="non_pagata">Non pagata</option>
          <option value="parziale">Parziale</option>
          <option value="pagata">Pagata</option>
          <option value="scaduta">Scaduta</option>
        </select>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-fattura-btn"
          className="flex items-center gap-1 bg-primary text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-primary/90">
          <Plus size={14} /> Nuova Fattura
        </button>
        <button onClick={scaricaElencoExcel} data-testid="export-fatture-excel"
          className="flex items-center gap-1 bg-emerald-600 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-emerald-700" title="Esporta elenco filtrato in Excel">
          <Download size={14} /> Excel
        </button>
        <button onClick={async () => {
          try {
            const { data } = await API.post('/api/sdi/sync/align-fatture');
            alert(`Sincronizzazione FattureInCloud completata.\n• Nuove fatture allineate: ${data.aligned || 0}\n• Re-sync esistenti: ${data.relinked || 0}\n• Saltate: ${data.skipped || 0}`);
            load();
          } catch (e) { alert('Errore sync: ' + (e.response?.data?.detail || e.message)); }
        }} className="flex items-center gap-1 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-indigo-700"
          title="Forza la sincronizzazione di stato SDI + pagamento da FattureInCloud" data-testid="sync-fic-btn">
          <RefreshCw size={14} /> Sync FIC
        </button>
      </div>

      {/* Tabella */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="text-left px-3 py-2.5 font-bold">Numero</th>
                <th className="text-left px-3 py-2.5 font-bold">Tipo</th>
                <th className="text-left px-3 py-2.5 font-bold">Data</th>
                <th className="text-left px-3 py-2.5 font-bold">Scadenza</th>
                <th className="text-left px-3 py-2.5 font-bold">Cliente</th>
                <th className="text-right px-3 py-2.5 font-bold">Totale</th>
                <th className="text-right px-3 py-2.5 font-bold">Pagato</th>
                <th className="text-center px-3 py-2.5 font-bold">Stato</th>
                <th className="text-center px-3 py-2.5 font-bold">SDI</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="10" className="text-center text-slate-400 py-8">Caricamento...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="10" className="text-center text-slate-400 py-8">Nessuna fattura trovata</td></tr>
              ) : filtered.map(f => (
                <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fattura-row-${f.id}`}>
                  <td className="px-3 py-2.5 font-bold">
                    {f.numero_fattura}
                    {f.tipo_fattura && f.tipo_fattura !== 'riepilogativa' && (
                      <span className={`ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${
                        f.tipo_fattura === 'acconto' ? 'bg-primary text-primary'
                          : f.tipo_fattura === 'saldo_finale' ? 'bg-green-100 text-green-700'
                          : f.tipo_fattura === 'sal' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                      }`}>
                        {f.tipo_fattura === 'saldo_finale' ? 'SALDO' : f.tipo_fattura.toUpperCase()}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${f.tipo === 'nota_credito' ? 'bg-red-100 text-red-700' : f.tipo === 'proforma' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                      {TIPO_LABEL[f.tipo]}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{fmtDate(f.data_emissione)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{fmtDate(f.data_scadenza)}</td>
                  <td className="px-3 py-2.5">{f.cliente_nome || '-'}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold">{fmt(f.totale_finale)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmt(f.importo_pagato)}</td>
                  <td className="px-3 py-2.5 text-center">
                    <select value={f.stato_pagamento} onChange={async (e) => {
                      try {
                        await API.patch(`/api/fatture/${f.id}/stato-pagamento`, { stato_pagamento: e.target.value });
                        load();
                      } catch (err) { alert('Errore: ' + (err.response?.data?.detail || err.message)); }
                    }}
                    data-testid={`stato-pag-select-${f.id}`}
                    className={`px-2 py-0.5 rounded text-xs font-bold border-0 cursor-pointer ${STATO_PAG[f.stato_pagamento]?.color}`}>
                      <option value="non_pagata">{STATO_PAG.non_pagata.label}</option>
                      <option value="parziale">{STATO_PAG.parziale.label}</option>
                      <option value="pagata">{STATO_PAG.pagata.label}</option>
                      <option value="scaduta">{STATO_PAG.scaduta.label}</option>
                    </select>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {f.tipo !== 'proforma' ? (
                      <SdiBadge local={f.sdi_stato} message={f.sdi_errore} />
                    ) : <span className="text-[10px] text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setDetailId(f.id)} title="Dettagli / pagamenti" data-testid={`open-fattura-${f.id}`}
                        className="p-1.5 hover:bg-green-100 text-green-600 rounded"><CreditCard size={14} /></button>
                      <button onClick={() => scaricaPdf(f)} title="PDF" data-testid={`pdf-${f.id}`}
                        className="p-1.5 hover:bg-slate-100 rounded"><FileDown size={14} /></button>
                      <button onClick={() => scaricaExcel(f)} title="Excel" data-testid={`xlsx-${f.id}`}
                        className="p-1.5 hover:bg-emerald-100 text-emerald-700 rounded"><Download size={14} /></button>
                      {f.tipo !== 'proforma' && f.stato_pagamento !== 'pagata' && (
                        <button onClick={() => apriSollecito(f)} title="Sollecito email" data-testid={`sollecito-${f.id}`}
                          className="p-1.5 hover:bg-amber-100 text-amber-700 rounded"><Send size={14} /></button>
                      )}
                      {f.tipo !== 'proforma' && (
                        <button onClick={() => apriXmlSdi(f)} title="Fattura Elettronica SDI (XML)" data-testid={`sdi-${f.id}`}
                          className={`p-1.5 rounded ${f.sdi_stato === 'generata' ? 'hover:bg-emerald-100 text-emerald-600' : 'hover:bg-indigo-100 text-indigo-600'}`}>
                          <FileCode2 size={14} />
                        </button>
                      )}
                      {f.tipo !== 'proforma' && (
                        <button onClick={() => pushToFic(f)} title={f.fic_id ? 'Aggiorna su FIC + invia SDI' : 'Invia su FattureInCloud + SDI'} data-testid={`push-fic-${f.id}`}
                          className={`p-1.5 rounded ${f.sdi_stato === 'accepted' ? 'hover:bg-emerald-100 text-emerald-700' : f.fic_id ? 'hover:bg-sky-100 text-sky-700' : 'hover:bg-primary/10 text-primary'}`}>
                          <Send size={14} />
                        </button>
                      )}
                      {f.fic_id && (
                        <button onClick={() => refreshFromFic(f)} title="Aggiorna stato pagamento/SDI da FattureInCloud (real-time)" data-testid={`refresh-fic-${f.id}`}
                          className="p-1.5 hover:bg-purple-100 text-purple-700 rounded">
                          <RefreshCw size={14} />
                        </button>
                      )}
                      <button onClick={() => { setEditing(f); setShowForm(true); }} title="Modifica"
                        className="p-1.5 hover:bg-blue-100 text-blue-600 rounded"><Edit3 size={14} /></button>
                      <button onClick={() => elimina(f.id)} title="Elimina" data-testid={`del-${f.id}`}
                        className="p-1.5 hover:bg-red-100 text-red-600 rounded"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <FatturaForm editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {detailId && <FatturaDetail id={detailId} onClose={() => { setDetailId(null); load(); }} />}
      {xmlModal && <XmlSdiModal fattura={xmlModal} onClose={() => { setXmlModal(null); load(); }} />}
      {sollecitoModal && <SollecitoModal data={sollecitoModal} onClose={() => { setSollecitoModal(null); load(); }} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SOLLECITO MODAL
// ═══════════════════════════════════════════════════════════════
function SollecitoModal({ data, onClose }) {
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);

  const apriClient = () => {
    window.location.href = data.mailto_url;
  };

  const inviaResend = async () => {
    setSending(true);
    try {
      const r = await API.post(`/api/fatture/${data.fattura_id}/sollecito/send`);
      setSentInfo(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore invio');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-5xl max-h-[92vh] overflow-y-auto shadow-2xl" data-testid="sollecito-modal">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-extrabold flex items-center gap-2"><Send size={18} /> Sollecito {data.fattura_num}</h2>
            <p className="text-xs text-slate-500">A: {data.cliente} ({data.to}) - {data.giorni_scadenza < 0 ? `Scaduta da ${Math.abs(data.giorni_scadenza)} giorni` : data.giorni_scadenza === 0 ? 'In scadenza oggi' : `Scade tra ${data.giorni_scadenza} giorni`}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Oggetto</label>
            <p className="text-sm bg-slate-50 p-2 rounded border border-slate-200">{data.subject}</p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Anteprima messaggio</label>
            <pre className="text-xs bg-slate-50 p-3 rounded border border-slate-200 whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">{data.body_text}</pre>
          </div>

          {sentInfo && (
            <div className={`p-3 rounded text-sm ${sentInfo.sent ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
              {sentInfo.sent ? `Email inviata (id: ${sentInfo.id})` : `Errore: ${sentInfo.error || sentInfo.reason}`}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
            <button onClick={apriClient} className="px-4 py-2.5 bg-primary text-white font-bold rounded flex items-center justify-center gap-2 hover:bg-primary/90" data-testid="open-mailto">
              <Mail size={16} /> Apri client mail (mailto)
            </button>
            {data.resend_configured ? (
              <button onClick={inviaResend} disabled={sending} className="px-4 py-2.5 bg-emerald-600 text-white font-bold rounded flex items-center justify-center gap-2 hover:bg-emerald-700 disabled:opacity-50" data-testid="send-resend">
                <Send size={16} /> {sending ? 'Invio...' : 'Invia ora (Resend)'}
              </button>
            ) : (
              <div className="px-4 py-2.5 bg-slate-100 text-slate-500 text-xs rounded text-center">
                Invio automatico disabilitato.<br />
                Configura RESEND_API_KEY per attivare.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// NOTIFICHE TAB
// ═══════════════════════════════════════════════════════════════
function NotificheTab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    API.get('/api/notifiche').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-400 py-8 text-center">Caricamento...</div>;

  const sevColor = (s) => s === 'high' ? 'bg-red-50 border-red-300 text-red-800' : s === 'medium' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800';
  const iconFor = (tipo) => tipo.startsWith('fattura') ? <Receipt size={16} /> : tipo.startsWith('evento') ? <CalIcon size={16} /> : <Bell size={16} />;

  return (
    <div className="space-y-3" data-testid="notifiche-tab">
      <div className="grid grid-cols-3 gap-3">
        <KpiFinance icon={AlertTri} label="Da gestire" value={data.totale} color="primary" />
        <KpiFinance icon={AlertCircle} label="Urgenti" value={data.high} color="red" />
        <KpiFinance icon={Bell} label="Medie" value={data.medium} color="amber" />
      </div>

      {data.notifiche.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-md p-8 text-center">
          <CheckCircle2 size={40} className="text-green-500 mx-auto mb-2" />
          <p className="font-bold text-green-800">Tutto in ordine!</p>
          <p className="text-sm text-green-600">Nessuna fattura scaduta o evento imminente.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.notifiche.map(n => (
            <div key={`${n.tipo}-${n.id}`} className={`border rounded-md p-3 flex items-start gap-3 ${sevColor(n.severity)}`} data-testid={`notifica-${n.id}`}>
              <div className="flex-shrink-0 mt-0.5">{iconFor(n.tipo)}</div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">{n.messaggio}</p>
                <p className="text-xs opacity-70 mt-0.5">
                  {n.cliente && `Cliente: ${n.cliente}`}
                  {n.cantiere_titolo && `Cantiere: ${n.cantiere_titolo}`}
                  {n.importo_residuo && ` - Residuo: ${fmt(n.importo_residuo)}`}
                </p>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/60 font-bold uppercase tracking-wider">{n.severity}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FatturaForm({ editing, onClose, onSaved }) {
  const [clienti, setClienti] = useState([]);
  const [preventivi, setPreventivi] = useState([]);
  const [fromPreventivo, setFromPreventivo] = useState(!editing);
  const [selectedPrev, setSelectedPrev] = useState('');
  const [modalita, setModalita] = useState('sintetica');
  const [defaults, setDefaults] = useState(null);
  const [data, setData] = useState(editing || {
    tipo: 'fattura',
    cliente_id: '',
    data_emissione: new Date().toISOString().slice(0, 10),
    data_scadenza: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    modalita_pagamento: 'MP05',
    condizioni_pagamento: 'TP02',
    iban: '',
    banca_nome: '',
    bic_swift: '',
    causale: '',
    oggetto: '',
    note: '',
    sconto_globale_pct: 0,
    sconto_globale_importo: 0,
    ritenuta_acconto_pct: 0,
    cassa_previdenziale_pct: 0,
    bollo_virtuale: 0,
    rivalsa_inps_pct: 0,
    scadenze: [],  // [{data, importo}]
    righe: [{ descrizione: '', quantita: 1, unita_misura: 'cad', prezzo_unitario: 0, sconto_pct: 0, aliquota_iva: 22 }],
  });
  const [saving, setSaving] = useState(false);
  const [regimiIvaZero, setRegimiIvaZero] = useState([]);

  // Carica clienti, preventivi e defaults aziendali
  useEffect(() => {
    API.get('/api/clienti').then(r => setClienti(r.data)).catch(() => {});
    API.get('/api/preventivi').then(r => setPreventivi(r.data.filter(p => ['approvato', 'validato'].includes(p.stato)))).catch(() => {});
    API.get('/api/sdi/regimi-iva-zero').then(r => setRegimiIvaZero(r.data?.regimi || [])).catch(() => {});
    API.get('/api/sdi/azienda/defaults').then(r => {
      setDefaults(r.data);
      // Pre-compila SOLO se siamo in modalità "nuovo" (non in editing)
      if (!editing) {
        setData(d => ({
          ...d,
          modalita_pagamento: r.data.modalita_pagamento || 'MP05',
          condizioni_pagamento: r.data.condizioni_pagamento || 'TP02',
          iban: r.data.iban || d.iban || '',
          banca_nome: r.data.banca_nome || '',
          bic_swift: r.data.bic_swift || '',
          note: r.data.note_pagamento || d.note || '',
          // Scadenza calcolata da giorni_scadenza
          data_scadenza: _calcScadenza(d.data_emissione, r.data.giorni_scadenza, r.data.scadenza_fine_mese),
        }));
      }
    }).catch(() => {});
  }, [editing]);

  function _calcScadenza(emissione, gg, fineMese) {
    try {
      const d = new Date(emissione);
      d.setDate(d.getDate() + (Number(gg) || 30));
      if (fineMese) {
        d.setMonth(d.getMonth() + 1);
        d.setDate(0);  // ultimo giorno del mese precedente
      }
      return d.toISOString().slice(0, 10);
    } catch (e) {
      return new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    }
  }

  const updRiga = (i, k, v) => {
    const rr = [...data.righe];
    rr[i] = { ...rr[i], [k]: v };
    // Se IVA torna > 0, svuoto la natura/dicitura per evitare incoerenze
    if (k === 'aliquota_iva' && Number(v) > 0) {
      rr[i].natura = '';
      rr[i].riferimento_normativo = '';
      rr[i]._regime_key = '';
    }
    setData({ ...data, righe: rr });
  };

  // Applica un regime IVA 0% (es. reverse_edile_servizi → N6.7 + dicitura)
  const applicaRegimeIvaZero = (i, regimeKey) => {
    const rr = [...data.righe];
    if (!regimeKey) {
      // Reset
      rr[i] = { ...rr[i], aliquota_iva: 22, natura: '', riferimento_normativo: '', _regime_key: '' };
    } else {
      const reg = regimiIvaZero.find(r => r.key === regimeKey);
      if (!reg) return;
      rr[i] = {
        ...rr[i],
        aliquota_iva: 0,
        natura: reg.natura,
        riferimento_normativo: reg.dicitura,
        _regime_key: reg.key,
      };
      // Auto-append dicitura nelle note testata (solo se non già presente)
      const dic = reg.dicitura;
      const noteAttuali = (data.note || '').trim();
      if (dic && !noteAttuali.includes(dic.slice(0, 30))) {
        const nuoveNote = noteAttuali ? `${noteAttuali}\n${dic}` : dic;
        setData(d => ({ ...d, note: nuoveNote, righe: rr }));
        return;
      }
    }
    setData({ ...data, righe: rr });
  };

  const addRiga = () => setData({ ...data, righe: [...data.righe, { descrizione: '', quantita: 1, unita_misura: 'cad', prezzo_unitario: 0, sconto_pct: 0, aliquota_iva: 22 }] });
  const delRiga = (i) => setData({ ...data, righe: data.righe.filter((_, x) => x !== i) });

  // Calcolo totali con sconto riga + sconto globale + cassa + ritenuta + bollo
  const totaleRighe = (data.righe || []).reduce((s, r) => {
    const q = Number(r.quantita) || 0;
    const p = Number(r.prezzo_unitario) || 0;
    const sc = Number(r.sconto_pct) || 0;
    return s + q * p * (1 - sc / 100);
  }, 0);

  const scontoGlobaleImp = (Number(data.sconto_globale_pct) || 0) > 0
    ? totaleRighe * (Number(data.sconto_globale_pct) / 100)
    : (Number(data.sconto_globale_importo) || 0);
  const imponibile = totaleRighe - scontoGlobaleImp;

  const cassaPrev = imponibile * (Number(data.cassa_previdenziale_pct) || 0) / 100;
  const imponibileConCassa = imponibile + cassaPrev;

  const totaleIva = (data.righe || []).reduce((s, r) => {
    const q = Number(r.quantita) || 0;
    const p = Number(r.prezzo_unitario) || 0;
    const sc = Number(r.sconto_pct) || 0;
    const ali = Number(r.aliquota_iva) || 0;
    // Riga proporzionalmente scontata e con cassa proporzionale
    const baseRiga = q * p * (1 - sc / 100);
    const proporzione = totaleRighe > 0 ? baseRiga / totaleRighe : 0;
    const baseConCassa = (imponibile * proporzione) + (cassaPrev * proporzione);
    return s + baseConCassa * ali / 100;
  }, 0);

  // Bollo virtuale: €2 se imponibile > 77,47€ e IVA = 0 e l'opzione è attiva
  const allRigheEsenti = (data.righe || []).every(r => (Number(r.aliquota_iva) || 0) === 0);
  const bolloAuto = defaults?.bollo_virtuale_automatico && allRigheEsenti && imponibile > 77.47 ? 2 : 0;
  const bollo = Number(data.bollo_virtuale) || bolloAuto;

  const ritenuta = imponibile * (Number(data.ritenuta_acconto_pct) || 0) / 100;

  const totaleFinale = imponibileConCassa + totaleIva + bollo - ritenuta;

  const salva = async () => {
    setSaving(true);
    try {
      // Allega i calcoli al payload per persistere i totali
      const payload = {
        ...data,
        totale_imponibile: round2(imponibile),
        totale_iva: round2(totaleIva),
        cassa_previdenziale_importo: round2(cassaPrev),
        bollo_virtuale: round2(bollo),
        ritenuta_acconto_importo: round2(ritenuta),
        totale_finale: round2(totaleFinale),
      };
      if (fromPreventivo && selectedPrev && !editing) {
        await API.post(`/api/fatture/da-preventivo/${selectedPrev}`, {
          tipo: data.tipo, modalita, data_emissione: data.data_emissione, data_scadenza: data.data_scadenza,
          modalita_pagamento: data.modalita_pagamento, iban: data.iban, oggetto: data.oggetto, note: data.note,
          banca_nome: data.banca_nome, bic_swift: data.bic_swift,
          condizioni_pagamento: data.condizioni_pagamento,
        });
      } else if (editing) {
        await API.put(`/api/fatture/${editing.id}`, payload);
      } else {
        await API.post('/api/fatture', payload);
      }
      onSaved();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const modPagOpts = defaults?.modalita_pagamento_options || { MP05: 'Bonifico', MP01: 'Contanti', MP02: 'Assegno' };
  const condPagOpts = defaults?.condizioni_pagamento_options || { TP01: 'A rate', TP02: 'Pagamento completo', TP03: 'Anticipo' };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md max-w-6xl w-full max-h-[90vh] overflow-auto" data-testid="fattura-form">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">{editing ? 'Modifica Fattura' : 'Nuova Fattura'}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {!editing && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3">
              <div className="flex items-center gap-4 mb-3">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input type="radio" checked={fromPreventivo} onChange={() => setFromPreventivo(true)} />
                  Da preventivo esistente
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input type="radio" checked={!fromPreventivo} onChange={() => setFromPreventivo(false)} />
                  Manuale
                </label>
              </div>
              {fromPreventivo && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select value={selectedPrev} onChange={(e) => setSelectedPrev(e.target.value)} data-testid="select-preventivo"
                    className="px-3 py-2 border border-slate-300 rounded-md text-sm">
                    <option value="">-- Seleziona preventivo --</option>
                    {preventivi.map(p => (
                      <option key={p.id} value={p.id}>{p.numero_preventivo} - {p.titolo || '(senza titolo)'} ({fmt(p.totale_finale)})</option>
                    ))}
                  </select>
                  <select value={modalita} onChange={(e) => setModalita(e.target.value)}
                    className="px-3 py-2 border border-slate-300 rounded-md text-sm">
                    <option value="sintetica">Fatturazione sintetica (1 riga)</option>
                    <option value="dettagliata">Dettagliata (tutte le righe)</option>
                  </select>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Tipo">
              <select value={data.tipo} onChange={(e) => setData({ ...data, tipo: e.target.value })} className="w-full input">
                <option value="fattura">Fattura</option>
                <option value="proforma">Proforma</option>
                <option value="nota_credito">Nota credito</option>
              </select>
            </Field>
            <Field label="Data emissione">
              <input type="date" value={typeof data.data_emissione === 'string' ? data.data_emissione.slice(0, 10) : ''}
                onChange={(e) => setData({ ...data, data_emissione: e.target.value })} className="w-full input" />
            </Field>
            <Field label="Data scadenza">
              <input type="date" value={typeof data.data_scadenza === 'string' ? data.data_scadenza.slice(0, 10) : ''}
                onChange={(e) => setData({ ...data, data_scadenza: e.target.value })} className="w-full input" />
            </Field>
          </div>

          {(!fromPreventivo || editing) && (
            <Field label="Cliente">
              <select value={data.cliente_id || ''} onChange={(e) => setData({ ...data, cliente_id: e.target.value })} className="w-full input">
                <option value="">-- Seleziona --</option>
                {clienti.map(c => (
                  <option key={c.id} value={c.id}>{c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`.trim()}</option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Oggetto">
            <input type="text" value={data.oggetto || ''} onChange={(e) => setData({ ...data, oggetto: e.target.value })} className="w-full input" />
          </Field>

          <Field label="Causale (visibile in fattura)">
            <input type="text" placeholder="Es. Saldo lavori cantiere Via Roma 12" value={data.causale || ''} onChange={(e) => setData({ ...data, causale: e.target.value })} className="w-full input" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Modalita pagamento (codice SDI)">
              <select value={data.modalita_pagamento} onChange={(e) => setData({ ...data, modalita_pagamento: e.target.value })} className="w-full input" data-testid="fattura-mod-pag">
                {Object.entries(modPagOpts).map(([k, v]) => <option key={k} value={k}>{k} - {v}</option>)}
              </select>
            </Field>
            <Field label="Condizioni pagamento (SDI)">
              <select value={data.condizioni_pagamento || 'TP02'} onChange={(e) => setData({ ...data, condizioni_pagamento: e.target.value })} className="w-full input">
                {Object.entries(condPagOpts).map(([k, v]) => <option key={k} value={k}>{k} - {v}</option>)}
              </select>
            </Field>
            <Field label="IBAN">
              <input type="text" value={data.iban || ''} onChange={(e) => setData({ ...data, iban: e.target.value.toUpperCase().replace(/\s/g, '') })} className="w-full input font-mono" />
            </Field>
            <Field label="Banca">
              <input type="text" placeholder="Es. Intesa Sanpaolo" value={data.banca_nome || ''} onChange={(e) => setData({ ...data, banca_nome: e.target.value })} className="w-full input" />
            </Field>
          </div>

          {(!fromPreventivo || editing) && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-bold">Righe</label>
                <div className="flex items-center gap-2">
                  {regimiIvaZero.find(x => x.key === 'reverse_edile_servizi') && (
                    <button type="button"
                      onClick={() => {
                        const reg = regimiIvaZero.find(x => x.key === 'reverse_edile_servizi');
                        if (!reg) return;
                        const newRighe = data.righe.map(r => ({
                          ...r, aliquota_iva: 0, natura: reg.natura,
                          riferimento_normativo: reg.dicitura, _regime_key: reg.key,
                        }));
                        const dic = reg.dicitura;
                        const noteAttuali = (data.note || '').trim();
                        const nuoveNote = (dic && !noteAttuali.includes(dic.slice(0, 30)))
                          ? (noteAttuali ? `${noteAttuali}\n${dic}` : dic)
                          : noteAttuali;
                        setData({ ...data, righe: newRighe, note: nuoveNote });
                      }}
                      className="text-[10px] font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 px-2 py-1 rounded inline-flex items-center gap-1"
                      data-testid="apply-reverse-edile-btn"
                      title="Applica Reverse Charge Edile (N6.7) a tutte le righe + dicitura legale automatica nelle Note">
                      ⚡ Reverse Charge Edile
                    </button>
                  )}
                  <button onClick={addRiga} className="text-xs font-bold text-primary hover:underline flex items-center gap-1"><Plus size={12} /> aggiungi</button>
                </div>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1">
                  <span className="col-span-4">Descrizione</span>
                  <span className="col-span-1">Q.tà</span>
                  <span className="col-span-1">U.M.</span>
                  <span className="col-span-2">Prezzo</span>
                  <span className="col-span-1">Sconto %</span>
                  <span className="col-span-2">IVA %</span>
                  <span className="col-span-1"></span>
                </div>
                {(data.righe || []).map((r, i) => {
                  const isZero = Number(r.aliquota_iva) === 0;
                  return (
                    <div key={i} className="space-y-1">
                      <div className="grid grid-cols-12 gap-2 items-start">
                        <input type="text" placeholder="Descrizione" value={r.descrizione} onChange={(e) => updRiga(i, 'descrizione', e.target.value)} className="col-span-4 input" />
                        <input type="number" placeholder="Q.ta" value={r.quantita} onChange={(e) => updRiga(i, 'quantita', e.target.value)} className="col-span-1 input" />
                        <input type="text" placeholder="U.M." value={r.unita_misura} onChange={(e) => updRiga(i, 'unita_misura', e.target.value)} className="col-span-1 input" />
                        <input type="number" step="0.01" placeholder="Prezzo" value={r.prezzo_unitario} onChange={(e) => updRiga(i, 'prezzo_unitario', e.target.value)} className="col-span-2 input" />
                        <input type="number" step="0.1" placeholder="Sc%" value={r.sconto_pct || 0} onChange={(e) => updRiga(i, 'sconto_pct', e.target.value)} className="col-span-1 input" />
                        <input type="number" placeholder="IVA %" value={r.aliquota_iva} onChange={(e) => updRiga(i, 'aliquota_iva', e.target.value)} className="col-span-2 input" data-testid={`riga-${i}-iva`} />
                        <button onClick={() => delRiga(i)} className="col-span-1 p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                      </div>
                      {isZero && (
                        <div className="ml-[33.33%] -mt-0.5 p-2 bg-amber-50 border border-amber-200 rounded text-xs flex items-start gap-2" data-testid={`riga-${i}-natura-box`}>
                          <AlertTri size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-amber-900">IVA 0% — selezionare regime:</span>
                              <select value={r._regime_key || ''} onChange={(e) => applicaRegimeIvaZero(i, e.target.value)}
                                className="text-xs px-2 py-1 border border-amber-300 rounded bg-white max-w-md"
                                data-testid={`riga-${i}-regime-select`}>
                                <option value="">— scegli regime —</option>
                                {regimiIvaZero.map(reg => (
                                  <option key={reg.key} value={reg.key}>{reg.natura} · {reg.label}</option>
                                ))}
                              </select>
                            </div>
                            {r.riferimento_normativo && (
                              <div className="text-[10px] text-amber-800 italic">📜 {r.riferimento_normativo}</div>
                            )}
                            {!r.natura && (
                              <div className="text-[10px] text-red-700 font-bold">⚠ Senza regime selezionato l'XML SDI verrà rifiutato</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sconto globale + Ritenuta + Cassa + Bollo */}
          {(!fromPreventivo || editing) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-slate-100 pt-3">
              <Field label="Sconto globale %">
                <input type="number" step="0.1" value={data.sconto_globale_pct || 0} onChange={(e) => setData({ ...data, sconto_globale_pct: e.target.value, sconto_globale_importo: 0 })} className="w-full input" />
              </Field>
              <Field label="Cassa previdenziale %">
                <input type="number" step="0.1" placeholder="Es. 4 per Inarcassa" value={data.cassa_previdenziale_pct || 0} onChange={(e) => setData({ ...data, cassa_previdenziale_pct: e.target.value })} className="w-full input" />
              </Field>
              <Field label="Ritenuta d'acconto %">
                <input type="number" step="0.1" placeholder="Es. 20" value={data.ritenuta_acconto_pct || 0} onChange={(e) => setData({ ...data, ritenuta_acconto_pct: e.target.value })} className="w-full input" />
              </Field>
              <Field label={`Bollo virtuale${bolloAuto > 0 ? ' (auto €2)' : ''}`}>
                <input type="number" step="0.01" placeholder={bolloAuto > 0 ? '2.00 (auto)' : '0.00'} value={data.bollo_virtuale || ''} onChange={(e) => setData({ ...data, bollo_virtuale: e.target.value })} className="w-full input" />
              </Field>
            </div>
          )}

          <Field label="Note">
            <textarea value={data.note || ''} onChange={(e) => setData({ ...data, note: e.target.value })} rows="2" className="w-full input" />
          </Field>

          {(!fromPreventivo || editing) && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-right space-y-1">
              <div className="text-sm"><span className="text-slate-500">Totale righe:</span> <span className="font-mono font-bold ml-2">{fmt(totaleRighe)}</span></div>
              {scontoGlobaleImp > 0 && (
                <div className="text-sm text-red-600"><span>– Sconto globale:</span> <span className="font-mono font-bold ml-2">{fmt(scontoGlobaleImp)}</span></div>
              )}
              <div className="text-sm"><span className="text-slate-500">Imponibile:</span> <span className="font-mono font-bold ml-2">{fmt(imponibile)}</span></div>
              {cassaPrev > 0 && (
                <div className="text-sm"><span className="text-slate-500">+ Cassa previdenziale:</span> <span className="font-mono font-bold ml-2">{fmt(cassaPrev)}</span></div>
              )}
              <div className="text-sm"><span className="text-slate-500">IVA:</span> <span className="font-mono font-bold ml-2">{fmt(totaleIva)}</span></div>
              {bollo > 0 && (
                <div className="text-sm"><span className="text-slate-500">+ Bollo virtuale:</span> <span className="font-mono font-bold ml-2">{fmt(bollo)}</span></div>
              )}
              {ritenuta > 0 && (
                <div className="text-sm text-red-600"><span>– Ritenuta d'acconto:</span> <span className="font-mono font-bold ml-2">{fmt(ritenuta)}</span></div>
              )}
              <div className="text-base border-t border-slate-300 pt-1 mt-1"><span className="font-bold">Totale fattura:</span> <span className="font-mono font-black ml-2 text-primary">{fmt(totaleFinale)}</span></div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold">Annulla</button>
          <button onClick={salva} disabled={saving} data-testid="save-fattura"
            className="px-4 py-2 rounded-md bg-primary text-white text-sm font-bold disabled:opacity-50">
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FatturaDetail({ id, onClose }) {
  const confirm = useConfirm();
  const [f, setF] = useState(null);
  const [newPag, setNewPag] = useState({ importo: '', modalita: 'bonifico', data_pagamento: new Date().toISOString().slice(0, 10), note: '' });

  const load = () => API.get(`/api/fatture/${id}`).then(r => setF(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const registra = async () => {
    if (!newPag.importo || Number(newPag.importo) <= 0) { alert('Importo non valido'); return; }
    try {
      await API.post(`/api/fatture/${id}/pagamenti`, newPag);
      setNewPag({ importo: '', modalita: 'bonifico', data_pagamento: new Date().toISOString().slice(0, 10), note: '' });
      load();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
  };

  const elimina = async (pid) => {
    if (!await confirm({ title: 'Eliminare pagamento?', message: 'Il pagamento verrà rimosso e l\'importo residuo della fattura ricalcolato.', confirmLabel: 'Elimina', variant: 'danger' })) return;
    await API.delete(`/api/fatture/${id}/pagamenti/${pid}`);
    load();
  };

  if (!f) return null;
  const residuo = Number(f.totale_finale || 0) - Number(f.importo_pagato || 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md max-w-6xl w-full max-h-[90vh] overflow-auto shadow-2xl" data-testid="fattura-detail">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">{f.numero_fattura}</h2>
            <p className="text-xs text-slate-500">{f.oggetto}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Totale" value={fmt(f.totale_finale)} />
            <StatCard label="Pagato" value={fmt(f.importo_pagato)} color="text-green-600" />
            <StatCard label="Residuo" value={fmt(residuo)} color={residuo > 0 ? 'text-red-600' : 'text-green-600'} />
            <StatCard label="Stato" value={STATO_PAG[f.stato_pagamento]?.label || f.stato_pagamento} />
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h3 className="text-sm font-bold mb-3">Registra nuovo pagamento</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <input type="number" placeholder="Importo" value={newPag.importo} onChange={(e) => setNewPag({ ...newPag, importo: e.target.value })} className="input" data-testid="pag-importo" />
              <input type="date" value={newPag.data_pagamento} onChange={(e) => setNewPag({ ...newPag, data_pagamento: e.target.value })} className="input" />
              <select value={newPag.modalita} onChange={(e) => setNewPag({ ...newPag, modalita: e.target.value })} className="input">
                <option value="bonifico">Bonifico</option>
                <option value="contanti">Contanti</option>
                <option value="assegno">Assegno</option>
                <option value="carta">Carta</option>
              </select>
              <button onClick={registra} data-testid="registra-pag-btn"
                className="bg-primary text-white rounded-md text-sm font-bold px-4 py-2 hover:bg-primary/90">Registra</button>
            </div>
            <input type="text" placeholder="Note (opz.)" value={newPag.note} onChange={(e) => setNewPag({ ...newPag, note: e.target.value })} className="input mt-2 w-full" />
          </div>

          <div>
            <h3 className="text-sm font-bold mb-2">Storico pagamenti ({(f.pagamenti || []).length})</h3>
            {(f.pagamenti || []).length === 0 ? (
              <p className="text-sm text-slate-400 py-3">Nessun pagamento registrato</p>
            ) : (
              <div className="space-y-1.5">
                {f.pagamenti.map(p => (
                  <div key={p.id} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2">
                    <div className="text-sm">
                      <span className="font-bold font-mono text-green-700">{fmt(p.importo)}</span>
                      <span className="text-slate-500 ml-3 text-xs">{fmtDate(p.data_pagamento)} · {p.modalita}</span>
                      {p.note && <span className="text-slate-400 text-xs ml-2">· {p.note}</span>}
                    </div>
                    <button onClick={() => elimina(p.id)} className="p-1 hover:bg-red-100 text-red-500 rounded"><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-3">
      <p className="text-[10px] font-bold text-slate-400 uppercase">{label}</p>
      <p className={`text-base font-black font-mono mt-0.5 ${color || ''}`}>{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SPESE
// ═══════════════════════════════════════════════════════════════
function SpeseTab({ anno }) {
  const confirm = useConfirm();
  const [spese, setSpese] = useState([]);
  const [filtri, setFiltri] = useState({ categoria: '', pagata: '' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const params = new URLSearchParams({ anno: String(anno) });
      if (filtri.categoria) params.set('categoria', filtri.categoria);
      if (filtri.pagata !== '') params.set('pagata', filtri.pagata);
      const r = await API.get(`/api/spese?${params.toString()}`);
      setSpese(r.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, [anno, filtri.categoria, filtri.pagata]);

  const elimina = async (id) => {
    if (!await confirm({ title: 'Eliminare spesa?', message: 'La spesa verrà rimossa dalla contabilità.', confirmLabel: 'Elimina', variant: 'danger' })) return;
    await API.delete(`/api/spese/${id}`);
    load();
  };

  const togglePagata = async (s) => {
    await API.put(`/api/spese/${s.id}`, { pagata: !s.pagata, data_pagamento: !s.pagata ? new Date().toISOString() : null });
    load();
  };

  const totaleTotale = spese.reduce((s, x) => s + Number(x.importo_totale || 0), 0);
  const totalePagate = spese.filter(s => s.pagata).reduce((s, x) => s + Number(x.importo_totale || 0), 0);

  return (
    <div data-testid="spese-tab">
      <div className="bg-white border border-slate-200 rounded-md p-3 mb-4 flex flex-wrap items-center gap-2">
        <select value={filtri.categoria} onChange={(e) => setFiltri({ ...filtri, categoria: e.target.value })}
          data-testid="filter-categoria"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          <option value="">Tutte le categorie</option>
          {CATEGORIE_SPESA.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
        <select value={filtri.pagata} onChange={(e) => setFiltri({ ...filtri, pagata: e.target.value })}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          <option value="">Tutte</option>
          <option value="true">Pagate</option>
          <option value="false">Non pagate</option>
        </select>
        <div className="flex-1"></div>
        <div className="text-sm text-slate-600 hidden md:block">
          Totale: <span className="font-bold font-mono">{fmt(totaleTotale)}</span> ·
          <span className="text-green-600 ml-2">Pagate: <span className="font-bold font-mono">{fmt(totalePagate)}</span></span>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-spesa-btn"
          className="flex items-center gap-1 bg-primary text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-primary/90">
          <Plus size={14} /> Nuova Spesa
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="text-left px-3 py-2.5">Data</th>
                <th className="text-left px-3 py-2.5">Categoria</th>
                <th className="text-left px-3 py-2.5">Descrizione</th>
                <th className="text-left px-3 py-2.5">Fornitore</th>
                <th className="text-right px-3 py-2.5">Imponibile</th>
                <th className="text-right px-3 py-2.5">IVA</th>
                <th className="text-right px-3 py-2.5">Totale</th>
                <th className="text-center px-3 py-2.5">Pagata</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {spese.length === 0 ? (
                <tr><td colSpan="9" className="text-center text-slate-400 py-8">Nessuna spesa registrata</td></tr>
              ) : spese.map(s => (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`spesa-row-${s.id}`}>
                  <td className="px-3 py-2.5 text-slate-600">{fmtDate(s.data)}</td>
                  <td className="px-3 py-2.5">
                    <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-bold">
                      {CATEGORIE_SPESA.find(c => c.v === s.categoria)?.l || s.categoria}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">{s.descrizione}</td>
                  <td className="px-3 py-2.5 text-slate-600">{s.fornitore || '-'}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{fmt(s.importo_imponibile)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-500">{fmt(s.importo_iva)}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold">{fmt(s.importo_totale)}</td>
                  <td className="px-3 py-2.5 text-center">
                    <button onClick={() => togglePagata(s)} data-testid={`toggle-pag-${s.id}`}
                      className={`px-2 py-0.5 rounded text-xs font-bold ${s.pagata ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                      {s.pagata ? 'Si' : 'No'}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => { setEditing(s); setShowForm(true); }} className="p-1.5 hover:bg-blue-100 text-blue-600 rounded"><Edit3 size={14} /></button>
                      <button onClick={() => elimina(s.id)} data-testid={`del-spesa-${s.id}`} className="p-1.5 hover:bg-red-100 text-red-600 rounded"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <SpesaForm editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function SpesaForm({ editing, onClose, onSaved }) {
  const [data, setData] = useState(editing ? {
    ...editing,
    data: editing.data ? editing.data.slice(0, 10) : new Date().toISOString().slice(0, 10),
    data_pagamento: editing.data_pagamento ? editing.data_pagamento.slice(0, 10) : '',
  } : {
    data: new Date().toISOString().slice(0, 10),
    categoria: 'materiali',
    descrizione: '',
    fornitore: '',
    importo_imponibile: 0,
    aliquota_iva: 22,
    pagata: false,
    data_pagamento: '',
    metodo_pagamento: '',
    numero_documento: '',
    note: '',
  });
  const [saving, setSaving] = useState(false);

  const iva = (Number(data.importo_imponibile) || 0) * (Number(data.aliquota_iva) || 0) / 100;
  const totale = (Number(data.importo_imponibile) || 0) + iva;

  const salva = async () => {
    setSaving(true);
    try {
      if (editing) await API.put(`/api/spese/${editing.id}`, data);
      else await API.post('/api/spese', data);
      onSaved();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md max-w-4xl w-full max-h-[90vh] overflow-auto shadow-2xl" data-testid="spesa-form">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">{editing ? 'Modifica Spesa' : 'Nuova Spesa'}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Data"><input type="date" value={data.data} onChange={(e) => setData({ ...data, data: e.target.value })} className="input w-full" /></Field>
            <Field label="Categoria">
              <select value={data.categoria} onChange={(e) => setData({ ...data, categoria: e.target.value })} className="input w-full">
                {CATEGORIE_SPESA.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Descrizione"><input type="text" value={data.descrizione} onChange={(e) => setData({ ...data, descrizione: e.target.value })} className="input w-full" data-testid="spesa-desc" /></Field>
          <Field label="Fornitore"><input type="text" value={data.fornitore} onChange={(e) => setData({ ...data, fornitore: e.target.value })} className="input w-full" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Imponibile"><input type="number" value={data.importo_imponibile} onChange={(e) => setData({ ...data, importo_imponibile: e.target.value })} className="input w-full" data-testid="spesa-imponibile" /></Field>
            <Field label="Aliquota IVA %"><input type="number" value={data.aliquota_iva} onChange={(e) => setData({ ...data, aliquota_iva: e.target.value })} className="input w-full" /></Field>
            <Field label="N. documento"><input type="text" value={data.numero_documento || ''} onChange={(e) => setData({ ...data, numero_documento: e.target.value })} className="input w-full" /></Field>
          </div>
          <div className="bg-slate-50 rounded p-3 text-right text-sm space-y-1">
            <div><span className="text-slate-500">IVA:</span> <span className="font-mono font-bold ml-2">{fmt(iva)}</span></div>
            <div><span className="font-bold">Totale:</span> <span className="font-mono font-black text-primary ml-2">{fmt(totale)}</span></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Pagata">
              <select value={data.pagata ? 'true' : 'false'} onChange={(e) => setData({ ...data, pagata: e.target.value === 'true' })} className="input w-full">
                <option value="false">No</option>
                <option value="true">Si</option>
              </select>
            </Field>
            <Field label="Data pagamento"><input type="date" value={data.data_pagamento || ''} onChange={(e) => setData({ ...data, data_pagamento: e.target.value })} className="input w-full" disabled={!data.pagata} /></Field>
            <Field label="Metodo">
              <select value={data.metodo_pagamento || ''} onChange={(e) => setData({ ...data, metodo_pagamento: e.target.value })} className="input w-full" disabled={!data.pagata}>
                <option value="">--</option>
                <option value="bonifico">Bonifico</option>
                <option value="contanti">Contanti</option>
                <option value="carta">Carta</option>
                <option value="assegno">Assegno</option>
              </select>
            </Field>
          </div>
          <Field label="Note"><textarea value={data.note || ''} onChange={(e) => setData({ ...data, note: e.target.value })} rows="2" className="input w-full" /></Field>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold">Annulla</button>
          <button onClick={salva} disabled={saving} data-testid="save-spesa"
            className="px-4 py-2 rounded-md bg-primary text-white text-sm font-bold disabled:opacity-50">
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SCADENZARIO
// ═══════════════════════════════════════════════════════════════
function ScadenzarioTab() {
  const [sc, setSc] = useState(null);
  useEffect(() => {
    API.get('/api/contabilita/scadenzario').then(r => setSc(r.data)).catch(() => {});
  }, []);

  if (!sc) return <div className="text-slate-400 py-8 text-center">Caricamento...</div>;

  return (
    <div className="space-y-5" data-testid="scadenzario-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiFinance icon={AlertTriangle} label="Da incassare" value={fmt(sc.totale_da_incassare)} sub={`${sc.fatture_scadenza.length} fatture aperte`} color="amber" />
        <KpiFinance icon={AlertTriangle} label="Scaduto" value={fmt(sc.totale_scaduto)} sub="Da sollecitare" color="red" />
        <KpiFinance icon={TrendingDown} label="Spese da pagare" value={fmt(sc.totale_spese_da_pagare)} sub={`${sc.spese_da_pagare.length} spese`} color="red" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-bold flex items-center gap-2"><Receipt size={16} /> Fatture in scadenza</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Numero</th>
                <th className="text-left px-3 py-2">Cliente</th>
                <th className="text-left px-3 py-2">Scadenza</th>
                <th className="text-right px-3 py-2">Residuo</th>
                <th className="text-center px-3 py-2">Stato</th>
              </tr>
            </thead>
            <tbody>
              {sc.fatture_scadenza.length === 0 ? (
                <tr><td colSpan="5" className="text-center text-slate-400 py-6">Nessuna fattura in scadenza</td></tr>
              ) : sc.fatture_scadenza.map(f => {
                const gg = f.giorni_scadenza;
                const urgenza = gg !== null && gg < 0 ? 'bg-red-50 text-red-700' : gg !== null && gg <= 7 ? 'bg-amber-50 text-amber-700' : 'text-slate-600';
                return (
                  <tr key={f.id} className="border-t border-slate-100" data-testid={`scad-row-${f.id}`}>
                    <td className="px-3 py-2.5 font-bold">{f.numero_fattura}</td>
                    <td className="px-3 py-2.5">{f.cliente_nome || '-'}</td>
                    <td className={`px-3 py-2.5 ${urgenza.includes('text-') ? urgenza : ''}`}>
                      {fmtDate(f.data_scadenza)}
                      {gg !== null && (
                        <span className={`ml-2 text-xs font-bold px-1.5 py-0.5 rounded ${urgenza}`}>
                          {gg < 0 ? `${-gg}g scaduta` : gg === 0 ? 'Oggi' : `tra ${gg}g`}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold">{fmt(f.residuo)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${STATO_PAG[f.stato_pagamento]?.color}`}>
                        {STATO_PAG[f.stato_pagamento]?.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="text-base font-bold flex items-center gap-2"><TrendingDown size={16} /> Spese da pagare</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Data</th>
                <th className="text-left px-3 py-2">Categoria</th>
                <th className="text-left px-3 py-2">Descrizione</th>
                <th className="text-left px-3 py-2">Fornitore</th>
                <th className="text-right px-3 py-2">Importo</th>
              </tr>
            </thead>
            <tbody>
              {sc.spese_da_pagare.length === 0 ? (
                <tr><td colSpan="5" className="text-center text-slate-400 py-6">Tutte le spese risultano pagate</td></tr>
              ) : sc.spese_da_pagare.map(s => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="px-3 py-2.5">{fmtDate(s.data)}</td>
                  <td className="px-3 py-2.5">{CATEGORIE_SPESA.find(c => c.v === s.categoria)?.l || s.categoria}</td>
                  <td className="px-3 py-2.5">{s.descrizione}</td>
                  <td className="px-3 py-2.5 text-slate-600">{s.fornitore || '-'}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold">{fmt(s.importo_totale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// REPORT IVA
// ═══════════════════════════════════════════════════════════════
function IvaTab({ anno }) {
  const [trimestre, setTrimestre] = useState('');
  const [iva, setIva] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams({ anno: String(anno) });
    if (trimestre) q.set('trimestre', trimestre);
    API.get(`/api/contabilita/iva?${q.toString()}`).then(r => setIva(r.data)).catch(() => {});
  }, [anno, trimestre]);

  if (!iva) return <div className="text-slate-400 py-8 text-center">Caricamento...</div>;

  return (
    <div className="space-y-5" data-testid="iva-tab">
      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3">
        <Filter size={14} className="text-slate-400" />
        <span className="text-sm font-bold">Periodo:</span>
        <select value={trimestre} onChange={(e) => setTrimestre(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="trimestre-select">
          <option value="">Tutto l'anno {anno}</option>
          <option value="1">I trimestre (Gen-Mar)</option>
          <option value="2">II trimestre (Apr-Giu)</option>
          <option value="3">III trimestre (Lug-Set)</option>
          <option value="4">IV trimestre (Ott-Dic)</option>
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiFinance icon={TrendingUp} label="IVA vendite" value={fmt(iva.iva_vendite)} sub={`${iva.num_fatture} fatture · ${iva.num_note_credito} NC`} color="green" />
        <KpiFinance icon={TrendingDown} label="IVA acquisti" value={fmt(iva.iva_acquisti)} sub={`${iva.num_spese} spese`} color="red" />
        <KpiFinance icon={DollarSign} label={iva.iva_a_debito > 0 ? 'IVA a debito' : 'IVA a credito'}
          value={fmt(iva.iva_a_debito > 0 ? iva.iva_a_debito : iva.iva_a_credito)}
          sub={iva.iva_a_debito > 0 ? 'Da versare' : 'Da compensare'}
          color={iva.iva_a_debito > 0 ? 'red' : 'green'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="text-base font-bold mb-3">Vendite per aliquota</h3>
          {iva.vendite_per_aliquota.length === 0 ? <p className="text-slate-400 text-sm py-3">Nessun dato</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-slate-500 text-xs"><th className="text-left pb-2">Aliquota</th><th className="text-right pb-2">Imponibile</th><th className="text-right pb-2">IVA</th></tr></thead>
              <tbody>
                {iva.vendite_per_aliquota.map((r, i) => (
                  <tr key={r.aliquota || i} className="border-t border-slate-100">
                    <td className="py-2 font-bold">{r.aliquota}</td>
                    <td className="py-2 text-right font-mono">{fmt(r.imponibile)}</td>
                    <td className="py-2 text-right font-mono">{fmt(r.iva)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="text-base font-bold mb-3">Acquisti per aliquota</h3>
          {iva.acquisti_per_aliquota.length === 0 ? <p className="text-slate-400 text-sm py-3">Nessun dato</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-slate-500 text-xs"><th className="text-left pb-2">Aliquota</th><th className="text-right pb-2">Imponibile</th><th className="text-right pb-2">IVA</th></tr></thead>
              <tbody>
                {iva.acquisti_per_aliquota.map((r, i) => (
                  <tr key={r.aliquota || i} className="border-t border-slate-100">
                    <td className="py-2 font-bold">{r.aliquota}</td>
                    <td className="py-2 text-right font-mono">{fmt(r.imponibile)}</td>
                    <td className="py-2 text-right font-mono">{fmt(r.iva)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


function XmlSdiModal({ fattura, onClose }) {
  const [validation, setValidation] = useState(null);
  const [status, setStatus] = useState(null);
  const [xmlText, setXmlText] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const loadAll = async () => {
    setLoading(true); setError('');
    try {
      const [v, s] = await Promise.all([
        API.get(`/api/sdi/validate/${fattura.id}`),
        API.get(`/api/fatture/${fattura.id}/sdi-status`),
      ]);
      setValidation(v.data);
      setStatus(s.data);
      if (s.data.stato === 'generata') {
        const x = await API.get(`/api/fatture/${fattura.id}/xml/preview`, { responseType: 'text', transformResponse: r => r });
        setXmlText(x.data);
      } else {
        setXmlText('');
      }
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    setLoading(false);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [fattura.id]);

  const genera = async () => {
    setGenerating(true); setError('');
    try {
      await API.post(`/api/fatture/${fattura.id}/xml`);
      await loadAll();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    setGenerating(false);
  };

  const download = async () => {
    try {
      const resp = await API.get(`/api/fatture/${fattura.id}/xml/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = status?.filename || `Fattura_${fattura.numero_fattura}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Errore download XML'); }
  };

  const allErrs = [...(validation?.errors_cedente || []), ...(validation?.errors_cessionario || [])];
  const canGenerate = validation?.valid === true;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-5xl max-h-[92vh] sm:rounded-md flex flex-col rounded-t-2xl shadow-2xl" data-testid="xml-sdi-modal">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <FileCode2 size={20} className="text-indigo-600" /> Fattura Elettronica SDI
            </h2>
            <p className="text-xs text-slate-500">{fattura.numero_fattura} - {fmt(fattura.totale_finale)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {loading && <p className="text-slate-400 text-center py-6">Caricamento...</p>}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && validation && (
            <>
              {validation.valid ? (
                <div className="bg-green-50 border border-green-200 text-green-800 rounded p-3 text-sm flex items-start gap-2" data-testid="sdi-valid-ok">
                  <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Tutti i dati sono validi: puoi generare l'XML FatturaPA.</span>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded p-3 text-sm" data-testid="sdi-valid-errors">
                  <p className="font-bold flex items-center gap-2 mb-1"><AlertCircle size={14} /> Dati incompleti</p>
                  <ul className="list-disc pl-5 text-xs space-y-0.5">
                    {allErrs.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                  <p className="text-xs mt-2">Completa i dati in <b>Admin - Dati Azienda SDI</b> e/o nell'anagrafica <b>Cliente</b>, quindi riapri.</p>
                </div>
              )}

              {validation.warnings?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded p-3 text-xs">
                  {validation.warnings.map((w, i) => <p key={i}>· {w}</p>)}
                </div>
              )}

              {status && (
                <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
                  <p className="font-bold text-slate-800 mb-1">Stato XML</p>
                  {status.stato === 'generata' ? (
                    <div className="space-y-1 text-xs text-slate-700">
                      <p>· Generato: <span className="font-mono">{status.filename}</span></p>
                      <p>· Formato: <b>{status.formato}</b> ({status.tipo_documento})</p>
                      <p>· Progressivo invio: <span className="font-mono">{status.progressivo}</span></p>
                      <p>· Data generazione: {status.generata_il ? new Date(status.generata_il).toLocaleString('it-IT') : '-'}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">Nessun XML ancora generato.</p>
                  )}
                </div>
              )}

              {xmlText && (
                <div className="border border-slate-200 rounded overflow-hidden">
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider bg-slate-900 text-slate-300 font-bold flex items-center justify-between">
                    <span>Anteprima XML</span>
                    <span className="font-mono">{(xmlText.length / 1024).toFixed(1)} KB</span>
                  </div>
                  <pre className="text-[10px] leading-tight p-3 max-h-72 overflow-auto bg-slate-50 font-mono text-slate-700" data-testid="xml-preview">{xmlText}</pre>
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold">Chiudi</button>
          <div className="flex items-center gap-2">
            {status?.stato === 'generata' && (
              <button onClick={download} data-testid="download-xml-btn"
                className="px-4 py-2 rounded-md bg-slate-100 border border-slate-300 text-sm font-bold flex items-center gap-2 hover:bg-slate-200">
                <FileDown size={14} /> Scarica XML
              </button>
            )}
            <button onClick={genera} disabled={!canGenerate || generating} data-testid="generate-xml-btn"
              className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50 hover:bg-indigo-700">
              <FileCode2 size={14} /> {generating ? 'Generazione...' : (status?.stato === 'generata' ? 'Rigenera XML' : 'Genera XML')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FATTURE PASSIVE
// ═══════════════════════════════════════════════════════════════
const STATO_FP = {
  da_contabilizzare: { label: 'Da contabilizzare', color: 'bg-yellow-100 text-yellow-800' },
  contabilizzata: { label: 'Contabilizzata', color: 'bg-green-100 text-green-800' },
};

function FatturePassiveTab({ anno }) {
  const confirm = useConfirm();
  const [lista, setLista] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [filtri, setFiltri] = useState({ stato: '', search: '' });
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ anno: String(anno) });
      if (filtri.stato) params.set('stato', filtri.stato);
      const [r, s] = await Promise.all([
        API.get(`/api/fatture-passive?${params.toString()}`),
        API.get(`/api/fatture-passive/stats/overview?anno=${anno}`),
      ]);
      setLista(r.data);
      setStats(s.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anno, filtri.stato]);

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true); setUploadMsg('');
    let ok = 0, err = [];
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('auto_collega_fornitore', 'true');
        await API.post('/api/fatture-passive/upload', fd);
        ok++;
      } catch (e) {
        err.push(`${file.name}: ${e.response?.data?.detail || e.message}`);
      }
    }
    setUploading(false);
    setUploadMsg(`${ok} caricata/e ${err.length ? `- errori: ${err.join('; ')}` : ''}`);
    setTimeout(() => setUploadMsg(''), 5000);
    load();
  };

  const filtered = lista.filter(f => !filtri.search ||
    (f.numero_fattura || '').toLowerCase().includes(filtri.search.toLowerCase()) ||
    (f.cedente_denominazione || '').toLowerCase().includes(filtri.search.toLowerCase()) ||
    (f.fornitore_ragione_sociale || '').toLowerCase().includes(filtri.search.toLowerCase())
  );

  const elimina = async (id) => {
    if (!await confirm({ title: 'Eliminare fattura passiva?', message: 'Se c\'è una spesa contabilizzata collegata, va scollegata prima.', confirmLabel: 'Elimina', variant: 'danger' })) return;
    try { await API.delete(`/api/fatture-passive/${id}`); load(); }
    catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div data-testid="fatture-passive-tab">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <MiniStat label="Totale" value={stats.totale_fatture} color="text-slate-800" />
          <MiniStat label="Da contabilizzare" value={stats.da_contabilizzare} color="text-yellow-600" />
          <MiniStat label="Contabilizzate" value={stats.contabilizzate} color="text-green-600" />
          <MiniStat label="Imponibile" value={fmt(stats.totale_imponibile)} color="text-slate-800" small />
          <MiniStat label="Totale" value={fmt(stats.totale_finale)} color="text-primary" small />
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-md p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Cerca numero, fornitore..."
            data-testid="fp-search"
            value={filtri.search} onChange={(e) => setFiltri({ ...filtri, search: e.target.value })}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm" />
        </div>
        <select value={filtri.stato} onChange={(e) => setFiltri({ ...filtri, stato: e.target.value })}
          data-testid="fp-filter-stato"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          <option value="">Tutti gli stati</option>
          <option value="da_contabilizzare">Da contabilizzare</option>
          <option value="contabilizzata">Contabilizzata</option>
        </select>
        <label className="flex items-center gap-1 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-indigo-700 cursor-pointer" data-testid="fp-upload-btn">
          <Upload size={14} /> {uploading ? 'Caricamento...' : 'Carica XML/PDF'}
          <input type="file" multiple accept=".xml,.p7m,.zip,.pdf"
            onChange={(e) => handleUpload(e.target.files)} className="hidden" disabled={uploading} data-testid="fp-upload-input" />
        </label>
      </div>

      {uploadMsg && (
        <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded p-2 mb-3 text-sm" data-testid="fp-upload-msg">
          {uploadMsg}
        </div>
      )}

      {/* Tabella */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fp-table">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="text-left px-3 py-2.5 font-bold">Numero</th>
                <th className="text-left px-3 py-2.5 font-bold">Data</th>
                <th className="text-left px-3 py-2.5 font-bold">Fornitore</th>
                <th className="text-left px-3 py-2.5 font-bold">Categoria</th>
                <th className="text-right px-3 py-2.5 font-bold">Imponibile</th>
                <th className="text-right px-3 py-2.5 font-bold">IVA</th>
                <th className="text-right px-3 py-2.5 font-bold">Totale</th>
                <th className="text-center px-3 py-2.5 font-bold">Stato</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="text-center text-slate-400 py-8">Caricamento...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="9" className="text-center text-slate-400 py-8">Nessuna fattura passiva. Carica un XML SDI o un PDF per iniziare.</td></tr>
              ) : filtered.map(f => (
                <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`fp-row-${f.id}`} onClick={() => setDetailId(f.id)}>
                  <td className="px-3 py-2.5 font-bold">{f.numero_fattura || '-'}</td>
                  <td className="px-3 py-2.5 text-slate-600">{fmtDate(f.data_emissione)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      {f.fornitore_id ? <Link2 size={12} className="text-green-600" /> : <Unlink size={12} className="text-slate-400" />}
                      <span>{f.fornitore_ragione_sociale || f.cedente_denominazione || '-'}</span>
                    </div>
                    {f.cedente_piva && <p className="text-[10px] text-slate-400 font-mono">P.IVA {f.cedente_piva}</p>}
                  </td>
                  <td className="px-3 py-2.5"><span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{f.categoria_spesa}</span></td>
                  <td className="px-3 py-2.5 text-right font-mono">{fmt(f.totale_imponibile)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmt(f.totale_iva)}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold">{fmt(f.totale_finale)}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${STATO_FP[f.stato]?.color || 'bg-slate-100'}`}>
                      {STATO_FP[f.stato]?.label || f.stato}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setDetailId(f.id)} title="Dettagli" className="p-1.5 hover:bg-indigo-100 text-indigo-600 rounded">
                        <Eye size={14} />
                      </button>
                      <button onClick={() => elimina(f.id)} title="Elimina" className="p-1.5 hover:bg-red-100 text-red-600 rounded" data-testid={`fp-del-${f.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detailId && <FatturaPassivaDetail id={detailId} onClose={() => { setDetailId(null); load(); }} />}
    </div>
  );
}

function MiniStat({ label, value, color, small }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`${small ? 'text-base' : 'text-2xl'} font-black ${color || 'text-slate-800'}`}>{value}</p>
    </div>
  );
}

function FatturaPassivaDetail({ id, onClose }) {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [xmlText, setXmlText] = useState('');
  const [showXml, setShowXml] = useState(false);
  const [fornitori, setFornitori] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, f] = await Promise.all([
        API.get(`/api/fatture-passive/${id}`),
        API.get('/api/fornitori'),
      ]);
      setData(r.data);
      setFornitori(f.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const loadXml = async () => {
    if (xmlText) { setShowXml(!showXml); return; }
    try {
      const r = await API.get(`/api/fatture-passive/${id}/xml/preview`, { responseType: 'text', transformResponse: x => x });
      setXmlText(r.data);
      setShowXml(true);
    } catch (e) { alert('XML non disponibile'); }
  };

  const creaFornitore = async () => {
    setBusy(true); setErr('');
    try {
      await API.post(`/api/fatture-passive/${id}/crea-fornitore`);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const collegaFornitore = async (fornitore_id) => {
    setBusy(true); setErr('');
    try {
      await API.put(`/api/fatture-passive/${id}`, { fornitore_id });
      await load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const aggiornaCategoria = async (categoria_spesa) => {
    setBusy(true);
    try {
      await API.put(`/api/fatture-passive/${id}`, { categoria_spesa });
      await load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const creaSpesa = async () => {
    setBusy(true); setErr('');
    try {
      await API.post(`/api/fatture-passive/${id}/crea-spesa`, { pagata: false });
      await load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const scollega = async () => {
    if (!await confirm({ title: 'Scollegare spesa contabilizzata?', message: 'La spesa contabilizzata collegata verrà eliminata, ma la fattura passiva resta.', confirmLabel: 'Scollega ed elimina spesa', variant: 'warning' })) return;
    setBusy(true);
    try {
      await API.post(`/api/fatture-passive/${id}/scollega-spesa`);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const downloadXml = async () => {
    try {
      const r = await API.get(`/api/fatture-passive/${id}/xml`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename_originale || `fattura_passiva_${id}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Download XML fallito'); }
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="bg-white rounded-md p-6">Caricamento...</div></div>
  );
  if (!data) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-6xl max-h-[92vh] sm:rounded-md flex flex-col rounded-t-2xl" data-testid="fp-detail-modal">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <FileCode2 size={20} className="text-indigo-600" /> Fattura Passiva {data.numero_fattura || '(no n.)'}
            </h2>
            <p className="text-xs text-slate-500">{data.cedente_denominazione || '-'} - {fmt(data.totale_finale)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {err && <div className="bg-red-50 border border-red-200 text-red-800 rounded p-2 text-sm"><AlertCircle size={14} className="inline mr-1" />{err}</div>}

          {/* Dati principali */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <InfoCard label="Numero" value={data.numero_fattura || '-'} />
            <InfoCard label="Data emissione" value={fmtDate(data.data_emissione)} />
            <InfoCard label="Tipo" value={data.tipo_documento || '-'} sub={data.tipo_documento_label} />
            <InfoCard label="Formato SDI" value={data.formato || data.tipo_upload?.toUpperCase() || '-'} />
          </div>

          {/* Cedente */}
          <div className="border border-slate-200 rounded-md">
            <div className="bg-slate-50 px-3 py-2 text-xs font-bold uppercase text-slate-600 border-b">Fornitore emittente</div>
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div><span className="text-slate-500">Denominazione:</span> <b>{data.cedente_denominazione}</b></div>
              <div><span className="text-slate-500">P.IVA:</span> <span className="font-mono">{data.cedente_piva || '-'}</span></div>
              {data.cedente_cf && <div><span className="text-slate-500">CF:</span> <span className="font-mono">{data.cedente_cf}</span></div>}
              {data.dati?.cedente?.indirizzo && <div><span className="text-slate-500">Indirizzo:</span> {data.dati.cedente.indirizzo}{data.dati.cedente.numero_civico ? ` ${data.dati.cedente.numero_civico}` : ''}, {data.dati.cedente.cap} {data.dati.cedente.comune} ({data.dati.cedente.provincia})</div>}
            </div>
          </div>

          {/* Totali */}
          <div className="grid grid-cols-3 gap-3">
            <InfoCard label="Imponibile" value={fmt(data.totale_imponibile)} />
            <InfoCard label="IVA" value={fmt(data.totale_iva)} />
            <InfoCard label="Totale" value={fmt(data.totale_finale)} highlight />
          </div>

          {/* Azioni fornitore */}
          <div className="border border-slate-200 rounded-md p-3 space-y-2">
            <h3 className="text-xs font-bold uppercase text-slate-600 flex items-center gap-2"><Link2 size={12} /> Collegamento fornitore</h3>
            {data.fornitore ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded p-2 text-sm">
                <div>
                  <b>{data.fornitore.ragione_sociale}</b>
                  {data.fornitore.piva && <span className="text-xs text-slate-500 ml-2">P.IVA {data.fornitore.piva}</span>}
                </div>
                <button onClick={() => collegaFornitore('')} disabled={busy} className="text-xs text-red-600 hover:underline">Scollega</button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <select className="input flex-1 min-w-[200px]" onChange={(e) => e.target.value && collegaFornitore(e.target.value)} defaultValue="" data-testid="fp-select-fornitore">
                  <option value="">-- Collega a fornitore esistente --</option>
                  {fornitori.map(f => <option key={f.id} value={f.id}>{f.ragione_sociale}</option>)}
                </select>
                <span className="text-xs text-slate-400">oppure</span>
                <button onClick={creaFornitore} disabled={busy} data-testid="fp-crea-fornitore-btn"
                  className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                  <Plus size={14} /> Crea fornitore dai dati XML
                </button>
              </div>
            )}
          </div>

          {/* Categoria spesa */}
          <div className="border border-slate-200 rounded-md p-3 space-y-2">
            <h3 className="text-xs font-bold uppercase text-slate-600">Categoria spesa (suggerita AI)</h3>
            <select value={data.categoria_spesa || 'altro'} onChange={(e) => aggiornaCategoria(e.target.value)} disabled={busy} className="input w-full sm:w-64" data-testid="fp-categoria-select">
              {CATEGORIE_SPESA.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>

          {/* Righe */}
          {data.dati?.righe?.length > 0 && (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 text-xs font-bold uppercase text-slate-600">Righe ({data.dati.righe.length})</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Descrizione</th>
                      <th className="text-right px-3 py-2">Q.ta</th>
                      <th className="text-left px-3 py-2">UM</th>
                      <th className="text-right px-3 py-2">Prezzo</th>
                      <th className="text-right px-3 py-2">Totale</th>
                      <th className="text-right px-3 py-2">IVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.dati.righe.map((r, i) => (
                      <tr key={r.numero_linea || i} className="border-t">
                        <td className="px-3 py-2">{r.numero_linea || i + 1}</td>
                        <td className="px-3 py-2">{r.descrizione}</td>
                        <td className="px-3 py-2 text-right font-mono">{r.quantita?.toFixed(2)}</td>
                        <td className="px-3 py-2">{r.unita_misura}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmt(r.prezzo_unitario)}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmt(r.prezzo_totale)}</td>
                        <td className="px-3 py-2 text-right">{r.aliquota_iva?.toFixed(0)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* XML preview */}
          {data.tipo_upload === 'xml' && (
            <div>
              <button onClick={loadXml} className="text-xs text-indigo-600 hover:underline flex items-center gap-1" data-testid="fp-toggle-xml">
                <Eye size={12} /> {showXml ? 'Nascondi XML' : 'Mostra XML originale'}
              </button>
              {showXml && xmlText && (
                <pre className="mt-2 text-[10px] leading-tight p-3 bg-slate-900 text-slate-100 rounded max-h-60 overflow-auto font-mono">{xmlText}</pre>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2">
            {data.tipo_upload === 'xml' && (
              <button onClick={downloadXml} className="px-3 py-2 bg-slate-100 border border-slate-300 rounded-md text-sm font-bold flex items-center gap-1 hover:bg-slate-200" data-testid="fp-download-xml">
                <FileDown size={14} /> Scarica XML
              </button>
            )}
            <button onClick={onClose} className="px-3 py-2 border border-slate-300 text-sm font-semibold rounded-md">Chiudi</button>
          </div>
          <div>
            {data.spesa_id ? (
              <button onClick={scollega} disabled={busy}
                className="px-4 py-2 bg-amber-500 text-white rounded-md text-sm font-bold hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2" data-testid="fp-scollega-btn">
                <Unlink size={14} /> Scollega spesa
              </button>
            ) : (
              <button onClick={creaSpesa} disabled={busy}
                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-bold hover:bg-green-700 disabled:opacity-50 flex items-center gap-2" data-testid="fp-crea-spesa-btn">
                <Zap size={14} /> {busy ? 'Creazione...' : 'Contabilizza come spesa'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ label, value, sub, highlight }) {
  return (
    <div className={`border rounded p-2 ${highlight ? 'bg-primary/10 border-primary/30' : 'bg-slate-50 border-slate-200'}`}>
      <p className="text-[10px] font-bold uppercase text-slate-500">{label}</p>
      <p className={`text-sm font-bold ${highlight ? 'text-primary' : ''}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500">{sub}</p>}
    </div>
  );
}

